/**
 * Spacer node view (#249): invisible until hover, drag the block to resize.
 * Chrome classes `zw-spacer-chrome` / `-label` / `-handle`.
 * Window-level pointer listeners, never pointer capture, so a failed drag can't
 * lock clicks elsewhere.
 */

import type { Node as PMNode } from "prosemirror-model";
import type { EditorView, NodeView } from "prosemirror-view";
import {
  formatSpacerHeight,
  snapSpacerEm,
  type SpacerHeight,
} from "../../lib/zwdoc/spacerHeight";

const HEIGHT_RE = /^([0-9]*\.?[0-9]+)(em|px|rem)$/;

export function parseSpacerHeight(css: string): SpacerHeight {
  const match = HEIGHT_RE.exec(css);
  const value = Number.parseFloat(match?.[1] ?? "2");
  const unit = (match?.[2] ?? "em") as SpacerHeight["unit"];
  return { value: Number.isFinite(value) ? value : 2, unit };
}

/** Height in em at the editor's font size (px / rem converted). */
export function spacerHeightEm(
  height: SpacerHeight,
  fontSizePx: number,
  rootPx: number,
): number {
  if (height.unit === "em") return height.value;
  if (height.unit === "rem") return (height.value * rootPx) / fontSizePx;
  return height.value / fontSizePx;
}

export function spacerView(
  initial: PMNode,
  view: EditorView,
  getPos: () => number | undefined,
): NodeView {
  let node = initial;
  const dom = document.createElement("div");
  dom.className = "zw-spacer zw-prose-spacer";
  dom.setAttribute("aria-hidden", "true");

  const chrome = document.createElement("span");
  chrome.className = "zw-spacer-chrome";
  chrome.contentEditable = "false";
  const label = document.createElement("span");
  label.className = "zw-spacer-label";
  const handle = document.createElement("span");
  handle.className = "zw-spacer-handle";
  handle.setAttribute("role", "separator");
  chrome.append(label, handle);
  dom.append(chrome);

  const show = (css: string): void => {
    dom.style.height = css;
    label.textContent = css;
    chrome.title = `Space ${css} — drag to resize`;
    handle.setAttribute("aria-label", `Vertical space ${css}. Drag to resize.`);
  };
  show(String(node.attrs.height));

  let dragging = false;
  let startY = 0;
  let startEm = 0;
  let fontSize = 16;

  const currentCss = (event: PointerEvent): string =>
    formatSpacerHeight({
      value: snapSpacerEm(startEm + (event.clientY - startY) / fontSize),
      unit: "em",
    });

  const onMove = (event: PointerEvent): void => {
    if (dragging === false) return;
    event.preventDefault();
    show(currentCss(event));
  };

  const endDrag = (event: PointerEvent): void => {
    if (dragging === false) return;
    dragging = false;
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("pointerup", endDrag, true);
    window.removeEventListener("pointercancel", endDrag, true);
    dom.classList.remove("zw-spacer-active");
    const next = currentCss(event);
    const pos = getPos();
    if (next === node.attrs.height || pos === undefined) {
      show(String(node.attrs.height));
      return;
    }
    view.dispatch(
      view.state.tr.setNodeMarkup(pos, undefined, { height: next }),
    );
  };

  dom.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || dragging || view.editable === false) return;
    event.preventDefault();
    event.stopPropagation();
    dragging = true;
    startY = event.clientY;
    fontSize = fontSizeOrDefault(getComputedStyle(view.dom).fontSize);
    const rootPx = fontSizeOrDefault(
      getComputedStyle(document.documentElement).fontSize,
    );
    startEm = spacerHeightEm(
      parseSpacerHeight(String(node.attrs.height)),
      fontSize,
      rootPx,
    );
    dom.classList.add("zw-spacer-active");
    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerup", endDrag, true);
    window.addEventListener("pointercancel", endDrag, true);
  });

  return {
    dom,
    update(next) {
      if (next.type !== node.type) return false;
      node = next;
      if (dragging === false) show(String(node.attrs.height));
      return true;
    },
    // The node view owns its pointer handling while dragging.
    stopEvent: (event) =>
      event.type.startsWith("pointer") || event.type === "mousedown",
    ignoreMutation: () => true,
    destroy() {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", endDrag, true);
      window.removeEventListener("pointercancel", endDrag, true);
    },
  };
}

function fontSizeOrDefault(value: string): number {
  const size = Number.parseFloat(value);
  return Number.isFinite(size) && size > 0 ? size : 16;
}
